import React, { useEffect } from "react";
import classNames from "classnames";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Annotorious, ImageAnnotator } from "@annotorious/react";
import * as rasterizeHTML from "rasterizehtml";
import { useProfile } from "@/_actions/userActions";
import Notification from "@/components/overlays/Notification";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquareMore, MessageSquareShare } from "lucide-react";

import "@annotorious/react/annotorious-react.css";

export default function ReportBug() {
  const [showForm, setShowForm] = React.useState(false);
  const [showReport, setShowReport] = React.useState(false);
  const [showNotification, toggleNotification] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [errors, setErrors] = React.useState([]);
  const [comment, setComment] = React.useState("");
  const [takingScreenshot, setTakingScreenshot] = React.useState(false);
  const [imgData, setImgData] = React.useState("");
  const canvasRef = React.useRef(null);
  const { data: profileData } = useProfile();

  React.useEffect(() => {
    if (profileData?.email) {
      setEmail(profileData.email);
    }
  }, [profileData]);

  React.useEffect(() => {
    const handleWindowError = function (error, url, line) {
      const err = {
        acc: "error",
        data: "ERR:" + error + " URL:" + url + " L:" + line,
      };
      // setErrors((prev) => [...prev, err]);
    };
    if (typeof window !== "undefined") {
      window.onerror = handleWindowError;
    }
    return () => {
      if (typeof window !== "undefined") {
        window.onerror = null;
      }
    };
  }, []);

  const [isAtBottom, setIsAtBottom] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      const bottomReached =
        window.innerHeight + Math.ceil(window.scrollY) >=
        document.documentElement.scrollHeight - 50;

      setIsAtBottom(bottomReached);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Initial check in case the page loads at the bottom
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []); // Empty dependency array ensures this runs only on mount and unmount

  const captureScreen = async () => {
    setTakingScreenshot(true);
    // wait for layout change
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const html = document.documentElement;

    rasterizeHTML
      .drawHTML(html.innerHTML, null, {
        width: window.innerWidth,
        height: window.innerHeight,
      })
      .then((renderResult) => {
        if (canvasRef.current) {
          const context = canvasRef.current.getContext("2d");
          canvasRef.current.width = renderResult.image.width;
          canvasRef.current.height = renderResult.image.height;
          context.drawImage(renderResult.image, 0, 0);
          setImgData(canvasRef.current.toDataURL());
        }
        setTakingScreenshot(false);
      });
  };

  const submitReport = async (e) => {
    e.preventDefault();
    try {
      const data = {
        // ...sessionData, // <-- Remove or define sessionData
        errors,
        comment,
        location: window.location.pathname,
        browser: window.navigator.userAgent,
        email,
      };
      await fetch("https://hooks.zapier.com/hooks/catch/12692807/2xoyodo/", {
        method: "POST",
        body: JSON.stringify(data),
      });
      toggleNotification(true);
      setShowReport(false);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    return () => {
      setShowForm(false);
    };
  }, []);

  return (
    <>
      <div
        className={classNames(
          "fixed right-5 transition-all duration-300 ease-in-out",
          {
            "bottom-5": !isAtBottom,
            "bottom-20": isAtBottom,
          }
        )}
      >
        <Button
          type="button"
          onClick={() => setShowReport(true)}
          variant="outline"
        >
          <MessageSquareMore className="mr-2 h-4 w-4" />
          <span className="text-sm ">Ayuda</span>
        </Button>
      </div>

      {/* Replace Modal with Dialog for main report */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent
          className={
            classNames(takingScreenshot ? "hidden" : "") + " centerFullScreen"
          }
        >
          <DialogHeader>
            <DialogTitle>Reportar un error</DialogTitle>
          </DialogHeader>
          <div className="p-5">
            {!showForm ? (
              <div>
                <Button
                  type="button"
                  onClick={() => setShowForm(true)}
                  variant="destructive"
                  className="w-full "
                >
                  Reportar un error
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <div className="text-center">
                  <h2 className="text-2xl font-bold">Reportar un error</h2>
                  <p className="mt-4">
                    Si encuentras un error en la aplicación, por favor envía un
                    reporte para que podamos solucionarlo.
                  </p>
                </div>
                <form onSubmit={submitReport} className="space-y-4">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm/6 font-medium text-gray-900"
                    >
                      Email
                    </label>
                    <div className="mt-2">
                      <Input
                        id="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        type="email"
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="comment"
                      className="block text-sm/6 font-medium text-gray-900"
                    >
                      Descripción
                    </label>
                    <Textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      name="comment"
                      id="comment"
                      rows="4"
                    ></Textarea>
                  </div>
                  {/* TODO: Add screenshoot
                  <button
                    onClick={captureScreen}
                    type="button"
                    className="rounded-md w-full block bg-gray-100 hover:bg-gray-200"
                  >
                    Adjuntar captura de pantalla
                  </button> */}
                  <Button type="submit">Enviar</Button>
                </form>
              </div>
            )}
            <hr className="my-4 border-gray-200" />
            <div>
              <a
                href="https://wa.me/15557045174"
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  variant="outline"
                  className="bg-teal-600 text-white text-xs hover:bg-teal-700 hover:text-white w-full"
                >
                  <MessageSquareShare className="h-4 w-4" />Ó habla con un
                  ejecutivo a través de WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Notification
        open={showNotification}
        setOpen={toggleNotification}
        timeOut={10000}
        title="El reporte ha sido enviado con éxito"
      />

      <canvas className="hidden h-auto w-full" ref={canvasRef} />

      {/* Replace Modal with Dialog for screenshot annotation */}
      {imgData && (
        <Dialog open={true} onOpenChange={() => setImgData("")}>
          <DialogContent width="w-[1200px]">
            <div>
              <Annotorious>
                <ImageAnnotator containerClassName="annotation-layer">
                  <img src={imgData} className="block h-auto w-full" />
                </ImageAnnotator>
              </Annotorious>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
